/**
 * The matter edit form's draft model - the part worth testing on its own.
 *
 * The Overview's "Edit details" form used to have no model at all: each field
 * wrote straight into the parent's `editedMatterFields`, the status dropdown
 * wrote straight to the API, and Save re-derived a diff from whatever state
 * had survived. Pulling the draft, the diff and the date handling out here
 * gives the form ONE definition of "what did the user change", and gives that
 * definition a test.
 *
 * Two rules encoded here:
 *
 *   1. A draft is always strings. Every control (Input, Dropdown, DateTimePicker)
 *      round-trips through a string, so the draft holds the string and the diff
 *      does the coercion once, at the edge, where the payload is built.
 *   2. Dates are parsed and formatted in LOCAL time. `new Date("2026-08-06")`
 *      is parsed as UTC midnight and `toISOString()` converts back through UTC,
 *      so a plain date round-tripped through either lands on the previous day
 *      for anyone west of Greenwich. The picker hands us a local Date; we keep
 *      it local.
 */

/** Every field the matter edit form can change. Strings throughout. */
export interface MatterDraft {
  MatterName: string;
  MatterType: string;
  Description: string;
  /** `YYYY-MM-DD`, or a full ISO string when the stored value carried a time. */
  due_date: string;
  /** Numeric id as a string ('' when the matter has no status). */
  status_id: string;
}

const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/* ── Dates ──────────────────────────────────────────────────────── */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** True when the stored value carries a clock time, not just a calendar day. */
export function hasTimeComponent(raw?: string | null): boolean {
  return !!raw && /T\d{2}:\d{2}/.test(raw);
}

/** `YYYY-MM-DD` → local midnight. Anything else → parsed as given. */
export function parseMatterDate(raw?: string | null): Date | null {
  if (!raw) return null;
  const date = DATE_ONLY.test(raw) ? new Date(`${raw}T00:00:00`) : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Local-time `YYYY-MM-DD` - never via `toISOString`, which shifts the day. */
export function toYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Back to the wire, at the granularity the field already had: a matter whose
 * due date has always been a calendar day stays a calendar day, and one that
 * carries a time keeps it. Widening a date-only field to an ISO timestamp on
 * the first edit would be this form silently changing the data's meaning.
 */
export function serialiseMatterDate(date: Date | null, withTime: boolean): string {
  if (!date) return '';
  return withTime ? date.toISOString() : toYmd(date);
}

/* ── Draft ──────────────────────────────────────────────────────── */

/** Seed a draft from the loaded matter. The form never reads `matterData` again. */
export function draftFromMatter(matter: any): MatterDraft {
  return {
    MatterName: str(matter?.MatterName),
    MatterType: str(matter?.MatterType),
    Description: str(matter?.Description),
    due_date: str(matter?.due_date ?? matter?.DueDate),
    status_id: str(matter?.status_id ?? matter?.current_status_id),
  };
}

/** Compare two date strings by the instant they denote, not by their spelling. */
function sameDate(a: string, b: string): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const left = parseMatterDate(a);
  const right = parseMatterDate(b);
  if (!left || !right) return a === b;
  return left.getTime() === right.getTime();
}

/**
 * What actually changed, as the update payload.
 *
 * Only changed keys appear - `/matters/edit` merges, so sending untouched
 * fields is both noise and a way to clobber a value another session wrote
 * while this form sat open. Text is trimmed before comparison so that adding
 * and removing a space is not an edit.
 */
export function diffMatterDraft(draft: MatterDraft, matter: any): Record<string, any> {
  const original = draftFromMatter(matter);
  const payload: Record<string, any> = {};

  (['MatterName', 'MatterType', 'Description'] as const).forEach((key) => {
    const next = draft[key].trim();
    if (next !== original[key].trim()) payload[key] = next;
  });

  if (!sameDate(draft.due_date, original.due_date)) {
    // An emptied date is a real edit - null clears it, '' would not.
    payload.due_date = draft.due_date || null;
  }

  if (draft.status_id !== original.status_id) {
    const statusId = parseInt(draft.status_id, 10);
    if (!Number.isNaN(statusId)) payload.status_id = statusId;
  }

  return payload;
}

/** Validation the form blocks Save on. Keys match `MatterDraft`. */
export function validateMatterDraft(
  draft: MatterDraft,
  t: (key: string) => string,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!draft.MatterName.trim()) {
    errors.MatterName = t('Matter name is required');
  } else if (draft.MatterName.trim().length > 100) {
    errors.MatterName = t('Matter name must be less than 100 characters');
  }
  if (!draft.MatterType.trim()) {
    errors.MatterType = t('Matter type is required');
  }
  return errors;
}
